import { useEffect } from "react";
import { CustomOption } from "@/components/common/custom-selectors/CustomOption";
import { useQuery } from "@/components/common/helpers/ReactHooks";
import { useMemo } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import { Actions as FSS_REPORT_ACTIONS } from "../../store/actions/fssReportActions";
import InvalidPermissions from "../company-pages/InvalidPermissions";
import CISModal from "./CISModal";
import FssRow from "./FssRow";
import RegenerateFormsModal from "./RegenerateFormsModal";
import { ArrowUpTrayIcon } from "@heroicons/react/20/solid";
import ImportTaxFormModal from "./ImportTaxFormModal";

const FssReports = ({
	reportTypes,
	fssReports,
	yearOptions,
	userPermissions,
	downloadFSSReport,
	listFssReports,
	openRegenerateFormsModal,
	taxFormRows: rows,
	loading,
	jurisdiction,
	openTaxFormImportModal,
}) => {
	const [reportType, setReportType] = useQuery("report");
	const [year, setYear] = useQuery("year");

	const selectedReport = reportTypes?.find((r) => r.value == reportType);

	const taxFormRows = useMemo(() => {
		if (selectedReport?.taxFormRows) return selectedReport.taxFormRows;
		return rows;
	}, [reportType]);

	useEffect(() => {
		// set loaded right away to avoid a re-trigger
		if (reportType) {
			listFssReports({
				name: reportType,
				year: year,
			});
		}
	}, [reportType, year]);

	useEffect(() => {
		if (yearOptions && !year) setYear(yearOptions[yearOptions.length - 1]?.value);
		if (reportTypes && !reportType) setReportType(reportTypes[0]?.value);
	}, [yearOptions, reportTypes?.length]);

	// console.log('connected',connected,connectLink)
	if (userPermissions == "None") return <InvalidPermissions />;

	return (
		<>
			<CISModal />
			<ImportTaxFormModal />
			<RegenerateFormsModal />
			<div className="content bg-gray-lighter">
				<div className="row">
					<div className="col-sm-12">
						<div className="pull-left">
							<h1 className="page-heading">Tax Forms</h1>
						</div>

						{jurisdiction === "UK" && (
							<div className="pull-right">
								<button
									className="btn btn-default pull-right"
									onClick={() => openTaxFormImportModal("Tax Form Import")}
								>
									<ArrowUpTrayIcon className="h-7 w-7 mr-2 align-middle" />
									Import Tax Forms
								</button>
							</div>
						)}
					</div>
				</div>
			</div>

			<div className="content">
				<div className="col-xs-4 pl-0">
					<label htmlFor="material-text">Tax Form</label>
					<Select
						value={selectedReport}
						options={reportTypes}
						components={{ Option: CustomOption }}
						onChange={(selected) => {
							setReportType(selected.value);
						}}
						isLoading={loading}
						isDisabled={loading}
					/>
					<div className="clearfix" />
				</div>
				<div className="col-xs-4 pl-0">
					<label htmlFor="material-text">Tax Year</label>
					<Select
						value={yearOptions?.find((r) => r.value == year)}
						options={yearOptions}
						onChange={(selected) => {
							setYear(selected.value);
						}}
						isLoading={loading}
						isDisabled={loading}
					/>
					<div className="clearfix" />
				</div>
				{!loading && ["fs3", "fs7"].includes(reportType) && (
					<div className="col-xs-4 pl-0">
						{reportType == "fs3" && (
							<div
								className="btn btn-default pull-right"
								style={{ marginTop: 24, marginLeft: 10 }}
								onClick={() => {
									downloadFSSReport({
										name: reportType,
										year: year,
										employee: "all",
									});
								}}
							>
								Download All
							</div>
						)}
						<div
							className="btn btn-default pull-right"
							style={{ marginTop: 24 }}
							onClick={() =>
								openRegenerateFormsModal({ status: true, year: year, reportType: reportType })
							}
						>
							Regenerate Forms
						</div>
					</div>
				)}
			</div>

			<div className="clearfix" />

			<div className="content mb-32">
				{fssReports && (
					<div style={{ overflow: "auto" }}>
						<table
							id="table-timesheets"
							className="js-table-sections table table-hover"
						>
							<thead>
								<tr>
									<th style={{ width: "5%" }}></th>
									<th>Name</th>
									{taxFormRows?.map((row, index) => {
										return <th key={row.label}>{row.label}</th>;
									})}
									{/*<th>Net Amount</th>*/}
									{reportType == "fs3" && <th>Portal Access</th>}
									<th></th>
								</tr>
							</thead>
							<tbody>
								{fssReports.map((fssReport) => (
									<FssRow
										key={fssReport.index}
										{...{
											fssReport,
											reportType,
											taxFormRows,
										}}
									/>
								))}
							</tbody>
						</table>

						<div className="clearfix" />
					</div>
				)}
			</div>
		</>
	);
};

const mapStateToProps = (state) => {
	const reportTypes = state.jurisdiction?.taxForms;
	const taxFormRows = state.jurisdiction?.taxFormRows;

	const { jurisdiction } = state.company?.settings || {};

	const formatYear = (year) => {
		switch (jurisdiction) {
			case "UK":
				{
					const nextYear = parseInt(year) + 1;
					return `${year}/${String(nextYear).slice(2)}`;
				}
				break;
			default:
				return year;
		}
	};

	const yearOptions =
		state?.payPeriods?.periods
			?.map((payroll) => Number(payroll?.year))
			?.filter((predicate, index, array) => {
				return array.indexOf(predicate) === index;
			})
			?.map((year) => ({
				label: formatYear(year),
				value: year,
			})) || [];

	const userPermissions = state?.permissions?.my?.permissions?.taxForms;

	return {
		loading: state.fssReportUpdating,
		taxFormRows,
		reportTypes,
		yearOptions,
		fssReports: state.fssReports,
		userPermissions,
		jurisdiction: state.company?.settings?.jurisdiction,
		org: state?.company?.settings?.org,
		isAdmin: state?.my?.profile?.isAdmin,
	};
};

const mapDispatchToProps = (dispatch) => ({
	downloadFSSReport: (data) => dispatch(FSS_REPORT_ACTIONS.downloadFSSReport(data)),
	listFssReports: (data) => dispatch(FSS_REPORT_ACTIONS.listFssReports(data)),
	toggleV2TaxReports: () => dispatch(FSS_REPORT_ACTIONS.toggleV2TaxReports()),
	openTaxFormImportModal: (v) => dispatch(FSS_REPORT_ACTIONS.openTaxFormImportModal(v)),
	openRegenerateFormsModal: ({ status, year, reportType }) =>
		dispatch(FSS_REPORT_ACTIONS.openRegenerateFormsModal({ status, year, reportType })),
});

export default connect(mapStateToProps, mapDispatchToProps)(FssReports);
